import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { DocumentIcon } from '@/assets/static/icons/document';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import CheckBox from '@/components/basicComponents/checkbox';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import { Loader } from '@/components/loaders/loader';
import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { EllipsisWithTooltip } from '../basicComponents/ellipsisWithTooltip';
import { IAiAssets } from './types';

interface FileSelectorProps {
  filesForAiAssistant: IAiAssets[];
  selectedFiles: string[];
  fileExtensions: string[];
  showDragonDrop: boolean;
  drag: boolean;
  scrollable?: boolean;
  isAiAssistantOpen?: boolean;
  generateAnswersLoading?: boolean;
  setSelectedFiles: Dispatch<SetStateAction<string[]>>;
  setShowDragonDrop: Dispatch<SetStateAction<boolean>>;
  setDrag: Dispatch<SetStateAction<boolean>>;
  handleUploadFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDropHandler: (e: React.DragEvent<HTMLDivElement>) => void;
}

export const FileSelector = ({
  filesForAiAssistant,
  selectedFiles,
  fileExtensions,
  showDragonDrop,
  drag,
  scrollable = true,
  isAiAssistantOpen,
  generateAnswersLoading = false,
  setSelectedFiles,
  setShowDragonDrop,
  setDrag,
  handleUploadFile,
  onDropHandler
}: FileSelectorProps) => {
  const [isDropDownOpen, setIsDropDownOpen] = useState(true);

  return (
    <>
      <SelectedDocument onClick={() => setIsDropDownOpen(!isDropDownOpen)}>
        Documents Selected for AI Assistant
        {isDropDownOpen ? (
          <ArrowUp fill={themeVar('font.action')} />
        ) : (
          <ArrowDown style={{ width: '28px', height: '28px', padding: '7px' }} fill={themeVar('font.action')} />
        )}
      </SelectedDocument>
      <DocumentsListWrapper isDropDownOpen={isDropDownOpen}>
        <ExpandableWrapper isDropDownOpen={isDropDownOpen}>
          <>
            <UploadedDocumnts scrollable={scrollable}>
              {filesForAiAssistant.map((file) => (
                <UploadedDocumentItem key={file.id ? file.id : file.filename} disabled={generateAnswersLoading || !file.id}>
                  <CheckboxWrapper
                    id={file.filename}
                    onClick={() => {
                      if (generateAnswersLoading || !file.id) return;

                      const updatedSelectedFiles = selectedFiles.includes(file.filename)
                        ? selectedFiles.filter((el) => el !== file.filename)
                        : [...selectedFiles, file.filename];
                      setSelectedFiles(updatedSelectedFiles);
                    }}
                  >
                    <CheckBox id={file.filename} isActive={selectedFiles.includes(file.filename)} />
                    <FileNameWrapper>
                      <DocumentIconWrapper>
                        <DocumentIcon />
                      </DocumentIconWrapper>
                      <EllipsisWithTooltip title={file.filename} forceRecalculate={isAiAssistantOpen}>
                        {file.filename}
                      </EllipsisWithTooltip>
                    </FileNameWrapper>
                  </CheckboxWrapper>
                  {!file.id && (
                    <LoaderWrap>
                      <CustomLoader size={17} />
                      Processing
                    </LoaderWrap>
                  )}
                </UploadedDocumentItem>
              ))}
            </UploadedDocumnts>
            {!showDragonDrop && Boolean(filesForAiAssistant.length) && (
              <Browse onClick={() => setShowDragonDrop(true)}>
                <PlusIcon width={19} height={19} fill={themeVar('font.action')} />
                Add More Files
              </Browse>
            )}
            {showDragonDrop && (
              <DropFileArea
                id={'handle-upload-file'}
                handleUploadFile={handleUploadFile}
                onDropHandler={onDropHandler}
                drag={drag}
                setDrag={setDrag}
                onClose={() => setShowDragonDrop(false)}
                fileExtensions={fileExtensions}
                withIcon
              />
            )}
          </>
        </ExpandableWrapper>
      </DocumentsListWrapper>
    </>
  );
};

const SelectedDocument = styled.div`
  margin: 0 20px;
  padding: 10px 0;
  border-bottom: 1px solid ${themeVar('border.base')};
  font-weight: 400;
  font-size: 16px;
  color: ${themeVar('font.weak')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const UploadedDocumnts = styled.div<{ scrollable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 15px 20px 0 35px;
  margin-bottom: 5px;
  max-height: ${({ scrollable }) => (scrollable ? '120px' : '100%')};
  overflow-y: ${({ scrollable }) => (scrollable ? 'auto' : 'visible')};
  overscroll-behavior: none;
  font-weight: 600;
  font-size: 16px;
`;

const FileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UploadedDocumentItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 400;
  font-family: Blinker;
  color: ${themeVar('font.base')};
`;

const CustomLoader = styled(Loader)`
  padding: 0;
`;

const Browse = styled.div`
  font-weight: 600;
  color: ${themeVar('font.action')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4.5px;
  margin-top: 10px;
  padding: 0 20px;
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 9px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  margin-top: 5px;
  padding: 5px;
`;

const DocumentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DocumentsListWrapper = styled.div<{ isDropDownOpen: boolean }>`
  display: grid;
  grid-template-rows: ${({ isDropDownOpen }) => (isDropDownOpen ? '1fr' : '0fr')};
  overflow: ${({ isDropDownOpen }) => (isDropDownOpen ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const ExpandableWrapper = styled.div<{ isDropDownOpen: boolean }>`
  min-height: 0;
  opacity: ${({ isDropDownOpen }) => (isDropDownOpen ? '1' : '0')};
`;
